import React, { useState, useRef, useEffect } from 'react';
import './MultiSelect.css'; // Add styles to mimic the dropdown
import ChartCheckbox from '../Backoffice/Dashboard/Tools/ChartCheckbox';

const SelectMulti = ({ options = [], onSelectionChange, placeholder = "Select options", reset = false, autoSelect = [] }) => {
    const [selectedItems, setSelectedItems] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const previousAutoSelect = useRef(autoSelect);

    // Handle the individual checkbox changes in the dropdown
    const handleCheckboxChange = (isChecked, option) => {
        let updatedSelectedItems;
        if (isChecked) {
            updatedSelectedItems = [...selectedItems, option];
        } else {
            updatedSelectedItems = selectedItems.filter(item => item !== option);
        }
        setSelectedItems(updatedSelectedItems);

        if (onSelectionChange) {
            onSelectionChange(updatedSelectedItems);
        }
    };

    // Handle the header checkbox change
    const handleHeaderCheckboxChange = (isChecked) => {
        if (isChecked) {
            const allValues = options.map(option => option.value);
            setSelectedItems(allValues);
            if (onSelectionChange) {
                onSelectionChange(allValues);
            }
        } else {
            setSelectedItems([]);
            if (onSelectionChange) {
                onSelectionChange([]);
            }
        }
    };

    // Determine if the header checkbox should be checked
    const isHeaderChecked = selectedItems.length === options.length && options.length > 0;

    // Handle opening and closing of dropdown
    const toggleDropdown = () => {
        setIsOpen(prevOpen => !prevOpen);
    };

    // Close the dropdown when clicking outside
    const closeDropdown = () => {
        setIsOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                closeDropdown();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Reset selectedItems when reset prop changes
    useEffect(() => {
        if (reset) {
            setSelectedItems([]);
            if (onSelectionChange) {
                onSelectionChange([]);
            }
        }
    }, [reset]);

    // Automatically select items when autoSelect prop changes and options are stable
    useEffect(() => {
        if (options.length > 0 && Array.isArray(autoSelect) && JSON.stringify(autoSelect) !== JSON.stringify(previousAutoSelect.current)) {
            const validSelections = options
                .filter(option => autoSelect.includes(option.value))
                .map(option => option.value);

            setSelectedItems(validSelections);
            if (onSelectionChange) {
                onSelectionChange(validSelections);
            }

            // Update the previous autoSelect to avoid unnecessary re-renders
            previousAutoSelect.current = autoSelect;
        }
    }, [autoSelect, options, onSelectionChange]);

    // Calculate selected labels for display
    const selectedLabels = options
        .filter(option => selectedItems.includes(option.value))
        .map(option => option.label)
        .join(', ');

    return (
        <div className="select-multi-wrap" ref={dropdownRef}>
            <div className="select-multi" onClick={toggleDropdown}>
                <ChartCheckbox
                    size='14px'
                    initialChecked={isHeaderChecked}
                    onChange={handleHeaderCheckboxChange}
                    label={selectedLabels.length > 0 ? selectedLabels : placeholder}
                />
                <div className="select-multi-arrow">
                    {isOpen ? '▲' : '▼'}
                </div>
            </div>
            {isOpen && (
                <div className="select-multi-dropdown">
                    {options.map((option, index) => (
                        <ChartCheckbox
                            size='14px'
                            key={index}
                            label={option.label}
                            initialChecked={selectedItems.includes(option.value)}
                            onChange={(isChecked) => handleCheckboxChange(isChecked, option.value)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default SelectMulti;
